<template>
  <BaseDialog
    v-if="open"
    :open="open"
    role="dialog"
    aria-modal="true"
    @close="onClose"
  >
    <template #title>
      Schuleinstellungen
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <div
        ref="dialogContentEl"
        class="dialog-content"
      >
        <div class="field grid-2 mb-0">
          <div>
            <label for="dayStart">Tagesbeginn</label>
            <FlatPickr
              id="dayStart"
              v-model="form.day_start_time"
              :config="timeConfig"
              class="flatpickr-datetime"
              :class="{ 'only--time': true }"
              :disabled="loading"
              :placeholder="'HH:MM'"
              required
            />
          </div>

          <div>
            <label for="presenceGrace">Anwesenheits‑Kulanz (Minuten)</label>
            <input
              id="presenceGrace"
              v-model.number="form.presence_grace_minutes"
              type="number"
              min="0"
              step="1"
              :disabled="loading"
            >
            <p class="help-text">
              Kulanz für Abwesenheit zum Angebots-Start.
            </p>
          </div>
        </div>

        <fieldset class="field maintenance-group">
          <legend>Wartung</legend>

          <div class="checkbox-row">
            <input
              id="appMaintenance"
              v-model="form.app_maintenance_enabled"
              type="checkbox"
              class="checkbox-24"
              :disabled="loading"
            >
            <div class="checkbox-content">
              <label for="appMaintenance">Wartungsmodus (komplette App)</label>
              <p class="help-text">
                Aktiviert Wartungsmodus. Alle Sessions werden abgemeldet.
              </p>
            </div>
          </div>

          <div class="checkbox-row">
            <input
              id="parentMaintenance"
              v-model="form.parent_features_maintenance_enabled"
              type="checkbox"
              class="checkbox-24"
              :disabled="loading"
            >
            <div class="checkbox-content">
              <label for="parentMaintenance">Wartungsmodus (Eltern: Kinder + Fehlzeiten)</label>
              <p class="help-text">
                Blendet die Kinderliste im Eltern-Dashboard und die Fehlzeiten aus (mit Ausnahmen).
              </p>
            </div>
          </div>

          <div
            v-if="form.parent_features_maintenance_enabled"
            class="mt-12"
          >
            <label for="parentExempt">Ausnahmen (Eltern)</label>
            <MultiSelect
              id="parentExempt"
              v-model="form.parent_features_maintenance_exempt_user_ids"
              :options="userOptions"
              option-label="label"
              option-value="value"
              :disabled="loading"
              placeholder="Eltern auswählen…"
              display="chip"
              class="w-full multiselect--clean"
              :filter="true"
              filter-placeholder="Suchen…"
              :append-to="dialogContentEl"
              panel-class="parent-exempt-panel"
            />
            <p class="help-text">
              Diese User sehen die Eltern-Funktionen weiterhin.
            </p>
          </div>
        </fieldset>

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>

        <div
          class="mt-12"
          style="display:flex;justify-content:flex-end;gap:8px;"
        >
          <button
            class="btn"
            type="button"
            :disabled="loading"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            class="btn btn-primary"
            type="submit"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner"
              aria-hidden="true"
            /> Speichern
          </button>
        </div>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {onMounted, reactive, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {schoolSettingsApi} from '@/services/api/schoolSettings';
import {useToast} from 'primevue/usetoast';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import MultiSelect from 'primevue/multiselect';
import api from '@/services/api';

const formEl = ref(null);

let dialogScrollEl = null;
let dialogScrollHandler = null;

const lastDialogScrollAt = ref(0);

function findScrollableParent(startEl) {
  let el = startEl;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    if (canScroll) return el;
    el = el.parentElement;
  }
  return null;
}

function bindDialogScrollGuard() {
  if (!formEl.value) return;

  const roleDialog = formEl.value.closest('[role="dialog"]');
  const candidate = findScrollableParent(formEl.value) || findScrollableParent(roleDialog) || roleDialog;

  dialogScrollEl = candidate;
  if (!dialogScrollEl) return;

  dialogScrollHandler = () => {
    lastDialogScrollAt.value = Date.now();
  };

  dialogScrollEl.addEventListener('scroll', dialogScrollHandler, {passive: true});
}

function unbindDialogScrollGuard() {
  if (dialogScrollEl && dialogScrollHandler) {
    dialogScrollEl.removeEventListener('scroll', dialogScrollHandler);
  }
  dialogScrollEl = null;
  dialogScrollHandler = null;
}

const timeConfig = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
};

const props = defineProps({
  open: {type: Boolean, default: false}
});
const emit = defineEmits(['close', 'saved']);
const toast = useToast();

const loading = ref(false);
const error = ref(null);

const userOptions = ref([]);

const form = reactive({
  day_start_time: '08:00',
  presence_grace_minutes: 0,

  app_maintenance_enabled: false,
  parent_features_maintenance_enabled: false,
  parent_features_maintenance_exempt_user_ids: []
});

function normalizeResponse(data) {
  // Unterstützt snake_case und camelCase
  const toTime = (v) => {
    if (!v) return null;
    // erwartet 'HH:MM' oder 'HH:MM:SS'
    const m = String(v).match(/^(\d{2}:\d{2})(?::\d{2})?$/);
    return m ? m[1] : null;
  };

  const exempt = data.parent_features_maintenance_exempt_user_ids
      ?? data.parentFeaturesMaintenanceExemptUserIds
      ?? [];

  const parents = data.parent_users ?? data.parentUsers ?? [];

  return {
    day_start_time: toTime(data.day_start_time ?? data.dayStart ?? data.start_time) || form.day_start_time,
    presence_grace_minutes: Number(
        data.presence_grace_minutes
        ?? data.presenceGraceMinutes
        ?? data.late_grace_minutes_default
        ?? data.lateGraceMinutesDefault
        ?? 0
    ),

    app_maintenance_enabled: Boolean(data.app_maintenance_enabled ?? data.appMaintenanceEnabled ?? false),
    parent_features_maintenance_enabled: Boolean(data.parent_features_maintenance_enabled ?? data.parentFeaturesMaintenanceEnabled ?? false),
    parent_features_maintenance_exempt_user_ids: Array.isArray(exempt) ? exempt.map((v) => Number(v)) : [],

    parent_users: Array.isArray(parents) ? parents : []
  };
}

async function loadUsers() {
  try {
    const {data} = await api.get('/users');
    const list = Array.isArray(data) ? data : (data?.users || []);

    const parentsOnly = list.filter((u) => {
      const roleId = Number(u.role_id ?? u.roleId ?? u.role ?? null);
      return roleId === 3; // Eltern
    });

    userOptions.value = parentsOnly
        .map((u) => {
          const first = String(u.first_name ?? u.firstName ?? '').trim();
          const last = String(u.last_name ?? u.lastName ?? '').trim();
          const display =
              String(u.display_name ?? u.displayName ?? '').trim()
              || [last, first].filter(Boolean).join(' ')
              || String(u.email ?? '').trim()
              || 'Elternteil';

          return {
            value: Number(u.id ?? u.user_id ?? u.userId),
            label: display
          };
        })
        .filter((x) => Number.isFinite(x.value))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'de', {sensitivity: 'base'}));
  } catch (e) {
    userOptions.value = [];
  }
}

async function load() {
  error.value = null;
  loading.value = true;
  try {
    const res = await schoolSettingsApi.getCurrent();
    const s = normalizeResponse(res.data || {});
    Object.assign(form, s);

    // Bevorzugt: Eltern aus Payload verwenden und nach Name sortieren
    if (Array.isArray(s.parent_users) && s.parent_users.length > 0) {
      userOptions.value = s.parent_users
          .map((p) => ({
            value: Number(p.value),
            label: String(p.label ?? '').trim()
          }))
          .filter((x) => Number.isFinite(x.value) && x.label)
          .sort((a, b) => String(a.label).localeCompare(String(b.label), 'de', {sensitivity: 'base'}));
    } else {
      // Fallback (falls Backend-Feld noch nicht vorhanden)
      await loadUsers();
    }

    // Vorselektion sicherstellen: nur IDs behalten, die in den Optionen existieren
    const allowed = new Set((userOptions.value || []).map((o) => Number(o.value)));
    form.parent_features_maintenance_exempt_user_ids = (form.parent_features_maintenance_exempt_user_ids || [])
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v) && allowed.has(v));
  } catch (e) {
    error.value = 'Einstellungen konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function onSubmit() {
  error.value = null;
  loading.value = true;
  try {
    const payload = {
      day_start_time: form.day_start_time,
      presence_grace_minutes: Number(form.presence_grace_minutes ?? 0),

      app_maintenance_enabled: Boolean(form.app_maintenance_enabled),
      parent_features_maintenance_enabled: Boolean(form.parent_features_maintenance_enabled),
      parent_features_maintenance_exempt_user_ids: (form.parent_features_maintenance_exempt_user_ids || []).map((v) => Number(v))
    };
    await schoolSettingsApi.update(payload);
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3000});
    emit('saved');
    emit('close');
  } catch (e) {
    error.value = 'Speichern fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (props.open) load();
});

watch(() => props.open, (v) => {
  if (v) load();
});

function onClose() {
  emit('close');
}

onMounted(() => {
  bindDialogScrollGuard();
  if (props.open) load();
});

watch(() => props.open, (v) => {
  if (v) {
    bindDialogScrollGuard();
    load();
  } else {
    unbindDialogScrollGuard();
    document.documentElement.classList.remove('keep-multiselect-panel-fixed');
  }
});
</script>

<style scoped>
/* Wenn das Panel beim Dialog-Scroll "fix" stehen soll */
:global(html.keep-multiselect-panel-fixed) :deep(.p-multiselect-panel) {
  position: fixed !important;
}

/* WICHTIG: Panel kann via appendTo/Portal außerhalb der Komponente liegen -> global stylen */
:global(.parent-exempt-panel .p-multiselect-items .p-multiselect-item),
:global(.parent-exempt-panel .p-multiselect-items .p-multiselect-option) {
  font-size: 14px;
  line-height: 20px;
  margin: 2px 0;
  padding: 4px 10px 4px 20px;

  background: color-mix(in srgb, floralwhite 35%, floralwhite) !important;
}

:global(.parent-exempt-panel .p-multiselect-items .p-multiselect-item:not(.p-highlight):hover),
:global(.parent-exempt-panel .p-multiselect-items .p-multiselect-option:not(.p-highlight):hover) {
  background: color-mix(in srgb, navajowhite 40%, var(--surface)) !important;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
}

@media (max-width: 767px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}

.maintenance-group {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
}

.maintenance-group > legend {
  padding: 0 6px;
  font-weight: 500;
}

.checkbox-row {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  align-items: start;
  padding: 8px 0;
}

.checkbox-content label {
  margin: 0;
}

.checkbox-24 {
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.help-text {
  font-size: 12px;
  color: black;
  border: 1px solid #ddd;
  background-color: #f8f8f8;
  padding: 3px 7px;
  border-radius: 4px;
}

/* PrimeVue MultiSelect: Dropdown/Panel optisch ruhiger + tokenbasiert */
:deep(.multiselect--clean.p-multiselect) {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);

  /* verhindert "zieht über den Dialog" */
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

:deep(.multiselect--clean.p-multiselect:not(.p-disabled):hover) {
  border-color: color-mix(in srgb, var(--border) 60%, var(--text));
}

:deep(.multiselect--clean.p-multiselect.p-focus) {
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

/* Container der Auswahl (Chips/Label): muss schrumpfen dürfen + scrollen */
:deep(.multiselect--clean .p-multiselect-label-container) {
  width: 100%;
  max-width: 100%;
  min-width: 0; /* wichtig für flex children, sonst overflow */
  box-sizing: border-box;

  max-height: 144px;
  overflow: auto; /* Scrollbars erzwingen bei Bedarf */
}

:deep(.multiselect--clean .p-multiselect-label) {
  padding: 8px 10px;
  font-size: 14px;
  line-height: 20px;
  color: var(--text);

  /* damit lange Labels/Chips nicht nach rechts rauslaufen */
  white-space: normal;
  overflow: hidden;
}

:deep(.p-multiselect-panel) {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}

:deep(.p-multiselect-token) {
  margin: 2px;
}

:deep(.p-multiselect-filter-container) {
  padding: 8px;
  border-bottom: 1px solid var(--border);
}

:deep(.p-multiselect-filter) {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 10px;
  background: var(--surface);
  color: var(--text);
}

.dialog-content {
  position: relative;
}

/* Panel relativ zum Dialog-Content fixieren */
:deep(.parent-exempt-panel.p-multiselect-panel) {
  position: absolute !important;
  top: 100px !important;

  /* volle Breite des Dialog-Contents; falls ihr Padding habt, wirkt das automatisch */
  left: 0 !important;
  right: 0 !important;

  z-index: 2000;
  max-height: min(360px, calc(100vh - 220px));
  overflow: auto;
}
</style>